import { useMemo, useState } from "react";
import { hotEvents, type HotEvent } from "@/mocks/communityData";
import SectionTitle from "./SectionTitle";

const categories = ["全部", "娱乐", "互联网", "社会", "饭圈", "数码", "职场"];

const heatColor: Record<string, string> = {
  爆: "bg-primary-500",
  热: "bg-secondary-500",
  新: "bg-accent-500",
  温: "bg-background-400 text-background-50",
};

export default function FocusSection() {
  const [active, setActive] = useState("全部");
  const [liked, setLiked] = useState<number[]>([]);

  const filtered = useMemo(
    () => (active === "全部" ? hotEvents : hotEvents.filter((e) => e.tag === active)),
    [active]
  );

  const toggleLike = (id: number) =>
    setLiked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const openDetail = (item: HotEvent) => {
    // 单页站点：点击话题后跳转到下方群组讨论区
    document.getElementById("forum")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="focus" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionTitle
          eyebrow="实时更新"
          title="今日焦点 · 全网热点实时播报"
          desc="热门事件不等人，这里按热度为你滚动更新全网正在发酵的瓜。想看哪类就点哪类，跟进不迷路。"
        />

        {/* category segmented control */}
        <div className="mb-8 flex flex-wrap items-center justify-center gap-2">
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setActive(cat)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition ${
                active === cat
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200/70"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* event list */}
        <div className="grid gap-4 md:grid-cols-2">
          {filtered.map((item) => (
            <article
              key={item.id}
              className="flex flex-col justify-between rounded-2xl border border-background-200 bg-background-50 p-5 transition hover:-translate-y-0.5 hover:border-primary-200 hover:bg-primary-50/20"
            >
              <div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-md px-2 py-0.5 text-xs font-bold text-background-50 ${heatColor[item.heat]}`}>
                    {item.heat}
                  </span>
                  <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-800">
                    {item.tag}
                  </span>
                  <span className="ml-auto flex items-center gap-1 text-xs text-foreground-400">
                    <i className="ri-time-line" aria-hidden="true"></i>
                    {item.time}
                  </span>
                </div>
                <h3 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
                  {item.title}
                </h3>
                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{item.summary}</p>
              </div>
              <div className="mt-4 flex items-center justify-between border-t border-background-100 pt-3">
                <div className="flex items-center gap-4 text-xs text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line text-sm" aria-hidden="true"></i>
                    {(item.reads / 1000).toFixed(1)}k 围观
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-3-line text-sm" aria-hidden="true"></i>
                    {item.comments} 讨论
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    aria-label="点赞"
                    onClick={() => toggleLike(item.id)}
                    className={`flex h-8 w-8 items-center justify-center rounded-full transition ${
                      liked.includes(item.id) ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-500 hover:bg-primary-100/70"
                    }`}
                  >
                    <i className={`${liked.includes(item.id) ? "ri-heart-fill" : "ri-heart-line"} text-sm`}></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => openDetail(item)}
                    className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-background-100 px-3 py-1.5 text-xs font-medium text-foreground-700 transition hover:bg-primary-500 hover:text-background-50"
                  >
                    去围观
                    <i className="ri-arrow-right-s-line text-sm" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="py-16 text-center text-sm text-foreground-500">该分类暂无进行中的热点，换个分类看看。</p>
        )}
      </div>
    </section>
  );
}